import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CustomersSearchIcon,
  EntitiesSearchIcon,
  InvoicesSearchIcon,
  ProjectsSearchIcon,
  PropertiesSearchIcon,
  UnitsSearchIcon,
  VouchersSearchIcon,
  ProductsSearchIcon,
  ContractsSearchIcon,
  LeadContactsSearchIcon,
  SupportCenterSearchIcon,
  UnitMessagesSearchIcon,
  CurrencyIcon,
} from "components/shared/icons";
import { SEARCH_MODULES } from "./searchConfig";
import { translate, LANGUAGE, getFormattedDateTime2 } from "services/general";

/**
 * Highlights matching text within a string by wrapping matches in a styled span.
 * @param {string} text - The text to search within
 * @param {string} query - The search query to highlight
 * @returns {React.ReactNode} - Text with highlighted matches
 */
const highlightText = (text, query) => {
  if (!text || !query || query.trim() === "") {
    return text;
  }

  const searchTerms = query.trim().toLowerCase();
  const textLower = text.toLowerCase();
  const startIndex = textLower.indexOf(searchTerms);

  if (startIndex === -1) {
    return text;
  }

  const endIndex = startIndex + searchTerms.length;
  const beforeMatch = text.slice(0, startIndex);
  const match = text.slice(startIndex, endIndex);
  const afterMatch = text.slice(endIndex);

  return (
    <>
      {beforeMatch}
      <span className="font-bold">{match}</span>
      {afterMatch.length > 0 && highlightText(afterMatch, query)}
    </>
  );
};

const SearchResultItem = ({ item, category, onClick, searchQuery = "" }) => {
  const { t } = useTranslation();

  // Helper to get currency code as string
  // eslint-disable-next-line no-unused-vars
  const getCurrencyCode = () => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return "ر.س";
      const user = JSON.parse(raw);
      return user?.agent?.currency?.code || user?.agent?.currency_code || "ر.س";
    } catch (e) {
      return "ر.س";
    }
  };

  const getIcon = () => {
    switch (category) {
      case "customers":
        return <CustomersSearchIcon />;
      case "entities":
        return <EntitiesSearchIcon />;
      case "invoices":
        return <InvoicesSearchIcon />;
      case "projects":
        return <ProjectsSearchIcon />;
      case "properties":
        return <PropertiesSearchIcon />;
      case "units":
        return <UnitsSearchIcon />;
      case "vouchers":
        return <VouchersSearchIcon />;
      case "products":
        return <ProductsSearchIcon />;
      case "contracts":
        return <ContractsSearchIcon />;
      case "lead_contacts":
        return <LeadContactsSearchIcon />;
      case "support_center":
        return <SupportCenterSearchIcon />;
      case "unit_messages":
        return <UnitMessagesSearchIcon />;
      default:
        return <CustomersSearchIcon />;
    }
  };

  const getRoute = () => {
    const module = SEARCH_MODULES[category];
    if (module && module.getRoute) {
      // Use original item data for route generation to ensure we have all fields
      const itemForRoute = item.original || item;
      return module.getRoute(itemForRoute);
    }
    return null;
  };

  // The link navigates; `onClick` only lets the search close itself. A modified click (new tab
  // or window) leaves the dropdown open so several results can be opened one after another.
  const handleLinkClick = (event) => {
    const isModified =
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0;
    if (!isModified) onClick?.(item, category);
  };

  const getSubtitle = () => {
    // For customers, show the translated type instead of phone number
    if (category === "customers" && item.type) {
      if (item.type === "individual") {
        return t("customers.types.individual");
      } else if (item.type === "company") {
        return t("customers.types.company");
      }
    }
    // For entities, show the translated type
    if (category === "entities" && item.type) {
      if (item.type === "supplier") {
        return t("entity_type.supplier");
      } else if (item.type === "organization") {
        return t("entity_type.organization");
      } else if (item.type === "utility") {
        return t("entity_type.utility");
      }
      // Fallback for any other entity types
      return t(`entity_type.${item.type}`, item.type);
    }
    // For properties, show "project_name · status" format
    if (category === "properties") {
      const parts = [];
      if (item.project_name || item.project_en_name) {
        parts.push(translate(item.project_name, item.project_en_name));
      }
      if (item.project_status) {
        // Translate project status
        const statusKey = `project.status.${item.project_status}`;
        const statusTranslation = t(statusKey);
        // Only add if translation exists (not the same as the key)
        if (statusTranslation && statusTranslation !== statusKey) {
          parts.push(statusTranslation);
        } else {
          // Fallback: use the status value directly if translation not found
          parts.push(item.project_status);
        }
      }
      if (parts.length > 0) {
        return parts.join(" · ");
      }
    }
    // For projects, show "district_name · total_units وحدة" format
    if (category === "projects") {
      const parts = [];
      if (item.district_name || item.en_district_name) {
        parts.push(translate(item.district_name, item.en_district_name));
      }
      if (
        item.total_units !== null &&
        item.total_units !== undefined &&
        item.total_units > 0
      ) {
        const unitsLabel = LANGUAGE === "ar" ? "وحدة" : "units";
        parts.push(`${item.total_units} ${unitsLabel}`);
      }
      if (parts.length > 0) {
        return parts.join(" · ");
      }
      // Fallback to status if district/units not available
      if (item.subtitle) {
        const statusKey = `project.status.${item.subtitle}`;
        const statusTranslation = t(statusKey);
        return statusTranslation !== statusKey
          ? statusTranslation
          : item.subtitle;
      }
    }
    // For vouchers, show "type · name" format (amount will be shown separately with icon)
    if (category === "vouchers") {
      const parts = [];
      if (item.type) {
        // Map receipt/payment to translation keys
        // receipt → credit (قبض/Receipt), payment → debit (صرف/Payment)
        let typeKey;
        if (item.type === "receipt") {
          typeKey = "credit";
        } else if (item.type === "payment") {
          typeKey = "debit";
        } else {
          // Handle other possible values
          typeKey = item.type === "credit" ? "credit" : "debit";
        }

        // Correct path: sanadat.list.types.credit or sanadat.list.types.debit
        const translationKey = `sanadat.list.types.${typeKey}`;
        const typeTranslation = t(translationKey);

        // Only use translation if it's different from the key (meaning translation was found)
        if (typeTranslation && typeTranslation !== translationKey) {
          parts.push(typeTranslation);
        } else {
          // Fallback: capitalize the type value
          parts.push(item.type.charAt(0).toUpperCase() + item.type.slice(1));
        }
      }
      if (item.subtitle) {
        parts.push(item.subtitle);
      }
      if (parts.length > 0) {
        return parts.join(" · ");
      }
    }
    // For invoices, just return the name (amount will be shown separately with icon)
    if (category === "invoices" && item.subtitle) {
      return item.subtitle;
    }
    // For unit messages, format created_at as date/time
    if (category === "unit_messages" && item.subtitle) {
      return getFormattedDateTime2(item.subtitle);
    }
    return item.subtitle;
  };

  const route = getRoute();
  const rowClassName =
    "flex items-center gap-3 p-3 hover:bg-surface-muted cursor-pointer transition-colors rounded-md min-w-0";

  const content = (
    <>
      {category === "customers" || category === "lead_contacts" ? (
        <img
          src={
            category === "lead_contacts" && item.original?.profile_image
              ? item.original.profile_image
              : "/image.png"
          }
          alt={category === "lead_contacts" ? "lead contact" : "customer"}
          className="flex-shrink-0 w-10 h-10 object-cover rounded-full"
          onError={(e) => {
            e.target.src = "/image.png";
          }}
        />
      ) : (
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
          {getIcon()}
        </div>
      )}
      <div className="flex-1 min-w-0 overflow-hidden">
        <p
          className="text-[#2F2D38] text-sm font-medium mb-1 truncate"
          title={item.name}
        >
          {highlightText(item.name, searchQuery)}
        </p>
        <p className="text-[#A3A5B4] text-xs flex items-center gap-1 min-w-0 overflow-hidden">
          <span className="truncate min-w-0">
            {highlightText(getSubtitle(), searchQuery)}
          </span>
          {item.amount && (
            <>
              <span className="flex-shrink-0">·</span>
              <span className="flex items-center gap-1 flex-shrink-0">
                {item.amount}
                <CurrencyIcon />
              </span>
            </>
          )}
        </p>
      </div>
    </>
  );

  // A result with no page of its own (no id, or a module without a detail route) keeps the
  // old behaviour of just closing the search.
  return route ? (
    <Link to={route} onClick={handleLinkClick} className={rowClassName}>
      {content}
    </Link>
  ) : (
    <div onClick={() => onClick?.(item, category)} className={rowClassName}>
      {content}
    </div>
  );
};

export default SearchResultItem;
